export const ONLINE_API_ORIGIN = 'https://user.api.zaparoo.com';
export const ONLINE_API_SPEC_URL = 'https://developers.zaparoo.com/openapi-user.yaml';

export const OnlineScopes = [
  'read:profile',
  'read:play_history',
  'read:cards',
  'read:decks',
  'read:devices',
  'read:backups',
] as const;

export type OnlineScope = (typeof OnlineScopes)[number];

export interface OnlineOperation {
  id: string;
  method: 'GET';
  path: string;
  scope?: OnlineScope;
  response: 'json' | 'binary';
}

export const OnlineOperations = [
  { id: 'status', method: 'GET', path: '/v1', response: 'json' },
  { id: 'profile', method: 'GET', path: '/v1/me', scope: 'read:profile', response: 'json' },
  {
    id: 'sessions.list',
    method: 'GET',
    path: '/v1/play-sessions',
    scope: 'read:play_history',
    response: 'json',
  },
  {
    id: 'sessions.active',
    method: 'GET',
    path: '/v1/play-sessions/active',
    scope: 'read:play_history',
    response: 'json',
  },
  {
    id: 'sessions.summary',
    method: 'GET',
    path: '/v1/play-sessions/summary',
    scope: 'read:play_history',
    response: 'json',
  },
  { id: 'cards.list', method: 'GET', path: '/v1/cards', scope: 'read:cards', response: 'json' },
  { id: 'decks.list', method: 'GET', path: '/v1/decks', scope: 'read:decks', response: 'json' },
  {
    id: 'decks.get',
    method: 'GET',
    path: '/v1/decks/{short_id}',
    scope: 'read:decks',
    response: 'json',
  },
  {
    id: 'decks.cards',
    method: 'GET',
    path: '/v1/decks/{short_id}/cards',
    scope: 'read:decks',
    response: 'json',
  },
  {
    id: 'devices.list',
    method: 'GET',
    path: '/v1/devices',
    scope: 'read:devices',
    response: 'json',
  },
  {
    id: 'backups.list',
    method: 'GET',
    path: '/v1/devices/{device_id}/backups',
    scope: 'read:backups',
    response: 'json',
  },
  {
    id: 'backups.files',
    method: 'GET',
    path: '/v1/devices/{device_id}/backups/{backup_id}/files',
    scope: 'read:backups',
    response: 'json',
  },
  {
    id: 'backups.download',
    method: 'GET',
    path: '/v1/devices/{device_id}/backups/{backup_id}/objects/{sha256}',
    scope: 'read:backups',
    response: 'binary',
  },
] as const satisfies readonly OnlineOperation[];

export type OnlineOperationId = (typeof OnlineOperations)[number]['id'];

export function onlineOperationPath(
  id: OnlineOperationId,
  parameters: Record<string, string> = {},
): string {
  const operation = OnlineOperations.find((candidate) => candidate.id === id);
  if (!operation) throw new Error(`Unknown Online User API operation ${id}`);
  return operation.path.replace(/\{([^}]+)\}/g, (_placeholder, name: string) => {
    const value = parameters[name];
    if (!value) throw new Error(`Online User API operation ${id} requires ${name}`);
    return encodeURIComponent(value);
  });
}
